import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Printer, ClipboardCheck, MessageCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtQty = (q) => Number(q).toLocaleString('en-US', { maximumFractionDigits: 3 });

export const ShoppingListDialog = ({ open, onOpenChange }) => {
  const [mode, setMode] = useState('created'); // 'created' | 'delivery' | 'range'
  const [date, setDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mode === 'delivery') {
        params.append('delivery_date', date);
      } else if (mode === 'range') {
        params.append('date', date);
        params.append('end_date', endDate);
      } else {
        params.append('date', date);
      }
      const r = await axios.get(`${API_URL}/reports/shopping-list?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load shopping list');
    } finally { setLoading(false); }
  }, [mode, date, endDate]);

  useEffect(() => { if (open) fetchList(); }, [open, fetchList]);

  const printList = () => window.print();

  const sendToWhatsApp = () => {
    if (!data || data.items.length === 0) return;
    const label = mode === 'delivery' ? `Scheduled ${date}` : mode === 'range' ? `${date} → ${endDate}` : `Placed ${date}`;
    const lines = [
      `🛒 *Bison shopping list* — ${label}`,
      `${data.totals.order_count} orders · ${data.totals.product_count} products · ${data.totals.customer_count} customers`,
      '',
      ...data.items.map(row => {
        const dest = row.destinations.map(d => `${d.customer} ${fmtQty(d.quantity)}`).join(', ');
        return `• ${row.product_name} — *${fmtQty(row.total_quantity)} ${row.unit}*\n   ↳ ${dest}`;
      }),
    ];
    const url = `https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto print:max-w-none print:shadow-none" data-testid="shopping-list-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-primary" />Shopping list</DialogTitle>
          <DialogDescription>
            Aggregated items to buy across selected orders — combined by product with per-customer destinations.
          </DialogDescription>
        </DialogHeader>

        <div className="print:hidden space-y-3">
          <RadioGroup value={mode} onValueChange={setMode} className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="created" data-testid="sl-mode-created" />Orders placed on</label>
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="delivery" data-testid="sl-mode-delivery" />Scheduled delivery on</label>
            <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="range" data-testid="sl-mode-range" />Date range</label>
          </RadioGroup>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">From</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sl-date-from" /></div>
            {mode === 'range' && <div><Label className="text-xs">To</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="sl-date-to" /></div>}
            <Button size="sm" variant="outline" onClick={sendToWhatsApp} className="ml-auto text-green-700 border-green-300 hover:bg-green-50" data-testid="sl-whatsapp"><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button>
            <Button size="sm" variant="outline" onClick={printList} data-testid="sl-print"><Printer className="w-4 h-4 mr-1" />Print</Button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : !data ? null : (
          <div className="space-y-4" id="shopping-list-print-area">
            <div className="flex flex-wrap gap-4 text-sm bg-muted/30 p-3 rounded-lg">
              <div><span className="text-muted-foreground">Orders:</span> <strong>{data.totals.order_count}</strong></div>
              <div><span className="text-muted-foreground">Distinct products:</span> <strong>{data.totals.product_count}</strong></div>
              <div><span className="text-muted-foreground">Customers:</span> <strong>{data.totals.customer_count}</strong></div>
              <div className="ml-auto text-xs text-muted-foreground">
                {mode === 'delivery' ? `Scheduled: ${date}` : mode === 'range' ? `${date} → ${endDate}` : `Placed: ${date}`}
              </div>
            </div>

            {data.items.length === 0 ? (
              <div className="text-center text-muted-foreground py-10">No items to shop for the selected filter.</div>
            ) : (
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden" data-testid="shopping-list-table">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2">Product</th>
                    <th className="text-right p-2 w-32">Total to buy</th>
                    <th className="text-left p-2">Destinations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.items.map(row => (
                    <tr key={row.product_id || row.product_name} data-testid={`sl-row-${row.product_id || row.product_name}`}>
                      <td className="p-2 font-medium">{row.product_name}</td>
                      <td className="p-2 text-right font-mono font-bold">{fmtQty(row.total_quantity)} {row.unit}</td>
                      <td className="p-2 text-xs">
                        {row.destinations.map((d, i) => (
                          <span key={i} className="inline-flex items-center gap-1 mr-2 mb-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                            {d.customer} · {fmtQty(d.quantity)} {row.unit}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
